import React, {
  memo, useCallback, useEffect, useMemo
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Modal from 'react/components/Modal/Modal';
import { getResourceTo } from 'react/utils/utils';
import { actionCreators } from 'react/api/actions/modals';

function LogDetailsModal() {
  const dispatch = useDispatch();

  const modalKey = useMemo(() => 'logDetails', []);
  const pendingText = useMemo(() => getResourceTo('eventLogHttpAction.status.pending'), []);

  const isOpen = useSelector((state) => state.modals.getIn([modalKey, 'isOpen']));
  const title = useSelector((state) => state.modals.getIn([modalKey, 'title']));
  const {
    url,
    date,
    httpStatus,
    requestBody,
    requestType,
    responseMessage,
    responseBody,
    isPending = false,
    entityDescription = null,
    organization = null
  } = useSelector((state) => state.modals.getIn([modalKey, 'data']).toJS());

  const successStatus = httpStatus >= 200 && httpStatus < 300;
  const isErrorStatus = httpStatus >= 400 && httpStatus <= 599;
  const isNotSuccessStatus = httpStatus < 200 || httpStatus >= 300;

  const formatRequestBody = useCallback((jsonText) => {
    try {
      const requestBodyParsed = JSON.parse(jsonText);

      return JSON.stringify(requestBodyParsed, null, 2).replace(/"/g, '');
    } catch (error) {
      return jsonText;
    }
  }, []);

  const getSafeValue = useCallback((value) => {
    if (isPending) return getResourceTo('eventLogHttpAction.status.pending');

    return value;
  }, [isPending]);

  const closeModal = useCallback(() => {
    dispatch(actionCreators.closeModal({
      modalKey
    }));
  }, []);

  const ModalFooter = (
    <div className="footerModal">
      <button
        data-testid="close-logdetails-modal"
        type="button"
        className="btn-secondary"
        onClick={closeModal}
      >
        {getResourceTo('default.button.close')}
      </button>
    </div>
  );

  function getContainer() {
    let container = document
      .querySelector('.mainContainer .reactivo-tabs.gmTabs #logsDetailsModalWrapper');

    if (!container) {
      container = document.createElement('div');
      container.id = 'logsDetailsModalWrapper';

      document.querySelector('.mainContainer .reactivo-tabs.gmTabs').appendChild(container);
    }

    return container;
  }

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeModal]);

  if (!url) {
    return null;
  }

  return (
    <Modal
      className="logDetailsModal"
      isOpen={isOpen}
      title={getResourceTo(title)}
      crossAxisBtn={false}
      footer={ModalFooter}
      closeModalFn={closeModal}
      parentSelector={() => getContainer()}
      shouldCloseOnOverlayClick
      onRequestClose={closeModal}
    >
      <div className="modalBody" data-testid="modal-logdetails-body">
        <div className="row">
          <div className="leftLogContent">
            <div className="sectionDetail date">
              <p className="titleDetail">{getResourceTo('integrationLog.field.date')}</p>
              <span data-testid="logdetails-date">{date}</span>
            </div>
            <div className="sectionDetail status">
              <p className="titleDetail">{getResourceTo('integrationLog.field.status')}</p>
              {isPending && (
                <div data-testid="logdetails-icon-pending" className="statusPending">
                  <span className="icon" />
                  <span className="text">{pendingText}</span>
                </div>
              )}
              {successStatus && !isPending && (
                <div data-testid="logdetails-icon-success" className="statusSuccess">
                  <span className="icon" />
                  <span className="text">{getResourceTo('IntegrationLogModal.SuccessText')}</span>
                </div>
              )}
              {(isErrorStatus || isNotSuccessStatus) && !isPending && (
                <div data-testid="logdetails-icon-error" className="statusError">
                  <span className="icon" />
                  <span className="text">{getResourceTo('IntegrationLogModal.ErrorText')}</span>
                </div>
              )}
            </div>
            {entityDescription && (
              <div className="sectionDetail entityDescription">
                <p className="titleDetail">{getResourceTo('integrationLog.field.entityDescription')}</p>
                <span data-testid="logdetails-entityDescription">{entityDescription}</span>
              </div>
            )}
            {organization && (
              <div className="sectionDetail organization">
                <p className="titleDetail">{getResourceTo('integrationLog.field.organization')}</p>
                <span data-testid="logdetails-organization">{organization}</span>
              </div>
            )}
            <div className="sectionDetail requestType">
              <p className="titleDetail">{getResourceTo('integrationLog.field.requestType')}</p>
              <span data-testid="logdetails-requestType">{getSafeValue(requestType)}</span>
            </div>
            <div className="sectionDetail requestCode">
              <p className="titleDetail">{getResourceTo('integrationLog.field.responseStatus')}</p>
              <span data-testid="logdetails-httpStatus">{getSafeValue(httpStatus)}</span>
            </div>
            <div className="sectionDetail requestMessage">
              <p className="titleDetail">{getResourceTo('integrationLog.field.responseMessage')}</p>
              <span data-testid="logdetails-responseMessage">{getSafeValue(responseMessage)}</span>
            </div>
            {responseBody && (
              <div className="sectionDetail responseMessage">
                <p className="titleDetail">{getResourceTo('integrationLog.field.responseBody')}</p>
                <span data-testid="logdetails-responseBody">{getSafeValue(responseBody)}</span>
              </div>
            )}
          </div>
          <div className="rightLogContent">
            <div className="sectionDetail requestUrl">
              <p className="titleDetail">{getResourceTo('integrationLog.field.url')}</p>
              <span data-testid="logdetails-url">{url}</span>
            </div>
            <div className="sectionDetail requestBody">
              <p className="titleDetail">{getResourceTo('integrationLog.field.requestBody')}</p>
              <span className="preRequest">
                <pre>{formatRequestBody(requestBody)}</pre>
              </span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default memo(LogDetailsModal);
